<!-- @component Shows a popup on the page, in one of -->

<script lang="ts">
  import { createDialog, melt } from "@melt-ui/svelte";
  import clsx from "clsx";
  import { XIcon } from "lucide-svelte";
  import type { Icon, IconProps } from "lucide-svelte";
  import { createEventDispatcher } from "svelte";
  import type { Component } from "svelte";
  import { cubicOut } from "svelte/easing";
  import { fade } from "svelte/transition";

  const dispatch = createEventDispatcher<{ close: void }>();

  export let open: boolean;
  export let title: string = "";
  export let size: string = "sm";
  export let centerTitle: boolean = false;

  /** Show a close button on the top-right of the Modal. */
  export let showCloseButton = false;

  export let icon: typeof Icon | Component<IconProps> | null = null;
  export let initialFocus: HTMLElement | null = null;

  const dimensions: { [key: string]: string } = {
    sm: "max-w-[600px] w-full max-h-[420px]",
    md: "max-w-[760px] w-full max-h-[800px]",
    lg: "max-w-screen-lg w-full",
  };

  $: dim = dimensions[size];

  const {
    elements: {
      overlay,
      content,
      title: titleEl,
      description,
      close,
      portalled,
    },
    states,
  } = createDialog({
    onOpenChange: ({ next }) => {
      // Ignore changes to `open`, and propagate them upward as events instead.
      // This turns the component into a controlled comonent, since the store's
      // value is always consistent with the `open` prop.
      if (open && !next) dispatch("close");
      return open;
    },
    openFocus: () => initialFocus,
  });

  $: states.open.set(open);
</script>

{#if open}
  <div use:melt={$portalled}>
    <div
      class="fixed z-40 inset-0 flex flex-col justify-center items-center p-4"
      transition:fade={{ duration: 200, easing: cubicOut }}
    >
      <div
        use:melt={$overlay}
        class="absolute inset-0 bg-ground/50 -z-10"
      ></div>
      <div
        class="relative overflow-hidden flex flex-col {dim} bg-white/5 border border-white/10 rounded-lg"
        use:melt={$content}
      >
        <div class="h-full p-6 sm:p-8 overflow-y-auto">
          {#if showCloseButton}
            <button
              bind:this={initialFocus}
              class="block btn -mt-1.5 -mr-1.5 p-1.5 float-right"
              use:melt={$close}
            >
              <XIcon size={20} />
            </button>
          {/if}

          {#if title}
            <div
              class={clsx(
                "flex items-center gap-x-2 mb-6",
                centerTitle && "justify-center",
              )}
            >
              {#if icon}
                <svelte:component
                  this={icon}
                  size={20}
                  class="flex-shrink-0 text-white opacity-60"
                />
              {/if}

              <h2
                class="text-xl leading-6 text-white font-medium"
                use:melt={$titleEl}
              >
                {title}
              </h2>
            </div>
          {/if}

          <div use:melt={$description} class="text-sm text-white">
            <slot />
          </div>
        </div>

        <div class="absolute inset-0 bg-ground -z-10"></div>
      </div>
    </div>
  </div>
{/if}
